import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, ScheduleDesignTokens } from '../types';
import type { CSSBlockCamel } from '../types/common';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

type ScheduleTokenSources = {
  unitFieldWidth?: string;
  runAtFieldWidth?: string;
  dateRangeFieldWidth?: string;
  fieldMarginRight?: string;
  sectionMarginBottom?: string;
  stepperMarginLeft?: string;
  stepperMarginRight?: string;
  runAtMarginRight?: string;
  unitLabelColor?: string;
  timezoneColor?: string;
  timezoneFontFamily?: string;
  timezoneFontSize?: string;
  timezoneFontWeight?: string | number;
  timezoneLineHeight?: string;
  popoverGap?: string;
  weekdayButtonWidth?: string;
  weekdayButtonHeight?: string;
  weekdayButtonDisabled?: CSSBlockCamel;
  warningMarginTop?: string;
  warningIconColor?: string;
  warningIconMarginRight?: string;
  warningTextColor?: string;
  warningFontFamily?: string;
  warningFontSize?: string;
  warningFontWeight?: string | number;
  warningLineHeight?: string;
};

const buildScheduleTokens = (
  sources: ScheduleTokenSources,
): ScheduleDesignTokens => ({
  layout: {
    unitFieldWidth: sources.unitFieldWidth,
    runAtFieldWidth: sources.runAtFieldWidth,
    dateRangeFieldWidth: sources.dateRangeFieldWidth,
    fieldMarginRight: sources.fieldMarginRight,
    sectionMarginBottom: sources.sectionMarginBottom,
    stepperMarginLeft: sources.stepperMarginLeft,
    stepperMarginRight: sources.stepperMarginRight,
    runAtMarginRight: sources.runAtMarginRight,
  },
  label: {
    unit: {
      color: sources.unitLabelColor,
    },
    timezone: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      color: sources.timezoneColor,
      fontFamily: sources.timezoneFontFamily,
      fontSize: sources.timezoneFontSize,
      fontWeight: sources.timezoneFontWeight,
      lineHeight: sources.timezoneLineHeight,
    },
    popoverGap: sources.popoverGap,
  },
  weekdaySelector: {
    size: {
      width: sources.weekdayButtonWidth,
      height: sources.weekdayButtonHeight,
    },
    button: {
      disabled: sources.weekdayButtonDisabled,
    },
  },
  monthlyWarning: {
    container: {
      display: 'flex',
      marginTop: sources.warningMarginTop,
    },
    icon: {
      color: sources.warningIconColor,
      marginRight: sources.warningIconMarginRight,
    },
    text: {
      color: sources.warningTextColor,
      fontFamily: sources.warningFontFamily,
      fontSize: sources.warningFontSize,
      fontWeight: sources.warningFontWeight,
      lineHeight: sources.warningLineHeight,
    },
  },
});

/**
 * Alloy weekday selector delegates geometry and selected styling to
 * `ButtonGroup` / `Button`. Only `size` and `button.disabled` are consumed.
 */
export const getScheduleTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): ScheduleDesignTokens => {
  const labelMedium = theme.typography.labelMedium;
  const bodySmall = theme.typography.bodySmall;

  return buildScheduleTokens({
    unitFieldWidth: '300px',
    runAtFieldWidth: '250px',
    dateRangeFieldWidth: '300px',
    fieldMarginRight: px(theme.spacing.M),
    sectionMarginBottom: px(theme.spacing.M),
    stepperMarginLeft: px(theme.spacing.XS),
    stepperMarginRight: px(theme.spacing.XS),
    runAtMarginRight: '10px',
    unitLabelColor: theme.tokens.colors.text.secondary.value,
    timezoneColor: theme.tokens.colors.text.secondary.value,
    timezoneFontFamily: labelMedium.fontFamily,
    timezoneFontSize: labelMedium.fontSize,
    timezoneFontWeight: labelMedium.fontWeight,
    timezoneLineHeight: labelMedium.lineHeight,
    popoverGap: '5px',
    weekdayButtonWidth: '36px',
    weekdayButtonHeight: '36px',
    weekdayButtonDisabled: {
      cursor: 'not-allowed',
    },
    warningMarginTop: '10px',
    warningIconColor: theme.tokens.colors.icon.warning.value,
    warningIconMarginRight: '5px',
    warningTextColor: theme.tokens.colors.text.secondary.value,
    warningFontFamily: bodySmall.fontFamily,
    warningFontSize: bodySmall.fontSize,
    warningFontWeight: bodySmall.fontWeight,
    warningLineHeight: bodySmall.lineHeight,
  });
};

/**
 * Weave weekday selector delegates geometry and selected styling to
 * `ButtonGroup` / `Button`. Only `size` and `button.disabled` are consumed.
 */
export const getScheduleTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): ScheduleDesignTokens => {
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const typography = tokens?.component?.typography;

  const bodyTypography = typography?.body;
  const captionTypography = typography?.caption;
  const mediumFontWeight = typography?.medium?.['font-weight'];

  return buildScheduleTokens({
    unitFieldWidth: '300px',
    runAtFieldWidth: '250px',
    dateRangeFieldWidth: '300px',
    fieldMarginRight: generic?.spacing?.[16],
    sectionMarginBottom: generic?.spacing?.[16],
    stepperMarginLeft: generic?.spacing?.[8],
    stepperMarginRight: generic?.spacing?.[8],
    runAtMarginRight: '10px',
    unitLabelColor: semantic?.['text-color']?.medium?.default,
    timezoneColor: semantic?.['text-color']?.medium?.default,
    timezoneFontFamily: fontFamily,
    timezoneFontSize: bodyTypography?.['font-size'],
    timezoneFontWeight: mediumFontWeight,
    timezoneLineHeight: bodyTypography?.['line-height'],
    popoverGap: generic?.spacing?.[4],
    weekdayButtonWidth: '36px',
    weekdayButtonHeight: '36px',
    weekdayButtonDisabled: {
      cursor: 'not-allowed',
    },
    warningMarginTop: generic?.spacing?.[8],
    warningIconColor: semantic?.['status-color']?.warning,
    warningIconMarginRight: generic?.spacing?.[4],
    warningTextColor: semantic?.['text-color']?.medium?.default,
    warningFontFamily: fontFamily,
    warningFontSize: captionTypography?.['font-size'],
    warningFontWeight: typography?.regular?.['font-weight'],
    warningLineHeight: captionTypography?.['line-height'],
  });
};
